import { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MessageSquare, ChevronDown, UserPlus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { TransferirAtendimentoButton } from './TransferirAtendimentoButton';
import { Janela24hBadge } from './Janela24hBadge';
import { useMensagens, useReagir } from '@/hooks/useMensagens';
import { useCliente, useAssumirAtendimento } from '@/hooks/useClientes';
import { useAuthContext } from '@/contexts/AuthContext';
import { useWhatsAppContact } from '@/hooks/useWhatsAppContact';
import { supabase } from '@/integrations/supabase/client';
import { markWhatsAppRead } from '@/services/whatsapp.service';
import { resolverCitacao, previewDeMensagem } from '@/lib/citacao';
import { toggleReaction } from '@/lib/reacoes';
import { canalDaConversa } from '@/lib/chat-app';
import { useTypingIndicator } from '@/hooks/useTypingIndicator';

interface ClienteData {
  nome?: string | null;
  avatar_url?: string | null;
  telefone?: string | null;
  email?: string | null;
  codigo_cliente?: string | null;
  responsavel_id?: string | null;
  responsavel?: { id: string; nome: string | null } | null;
  consumer_id?: string | null;
  origem?: string | null;
}

interface ChatWindowProps {
  clienteId?: string;
}

// Marca todas as mensagens de cliente como lidas numa conversa. Depende da
// policy `mensagens_update` adicionada em 20260423_fix_mensagens_update_rls.sql.
async function marcarMensagensLidas(clienteId: string): Promise<void> {
  const { error } = await supabase
    .from('mensagens')
    .update({ lida: true })
    .eq('cliente_potencial_id', clienteId)
    .eq('lida', false)
    .eq('remetente', 'cliente');
  if (error) throw error;
}

export function ChatWindow({ clienteId }: ChatWindowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const prevMessageCountRef = useRef(0);

  const { data: mensagens, isLoading: loadingMensagens } = useMensagens(clienteId);
  const { data: clienteRaw, isLoading: loadingCliente } = useCliente(clienteId);
  const { data: whatsAppContact } = useWhatsAppContact(clienteId);
  const cliente = clienteRaw as ClienteData | null | undefined;
  const { profile } = useAuthContext();
  type MensagemRow = NonNullable<typeof mensagens>[number];
  const [respostaA, setRespostaA] = useState<MensagemRow | null>(null);
  const queryClient = useQueryClient();
  const assumirAtendimento = useAssumirAtendimento();
  const reagir = useReagir();

  const ehApp = cliente ? canalDaConversa(cliente) === 'app' : false;
  const { otherTyping, notifyTyping } = useTypingIndicator(ehApp ? clienteId : undefined, 'empresa');

  // Keep the latest scroll position in a ref so the message-effect below can
  // read it without re-firing every time the user scrolls.
  const isAtBottomRef = useRef(true);
  useEffect(() => {
    isAtBottomRef.current = isAtBottom;
  }, [isAtBottom]);

  useEffect(() => {
    setNewMessageCount(0);
    // Reset the count tracker too — otherwise the next conversation's first
    // useEffect tick won't recognize it as the "initial load" case.
    prevMessageCountRef.current = 0;
    setRespostaA(null);
  }, [clienteId]);

  // bsp_message_id da última msg recebida do cliente — usado pelo read receipt
  // e pelo indicador "a escrever" (ambos referenciam uma msg recebida na Meta).
  const ultimaClienteBspId = useMemo(() => {
    if (!mensagens) return null;
    const ultima = [...mensagens]
      .reverse()
      .find((m) => m.remetente === 'cliente' && m.bsp_message_id);
    return ultima?.bsp_message_id ?? null;
  }, [mensagens]);

  useEffect(() => {
    if (!clienteId || !mensagens) return;
    const hasUnreadFromCliente = mensagens.some(
      (m) => m.remetente === 'cliente' && !m.lida,
    );
    if (hasUnreadFromCliente) {
      marcarMensagensLidas(clienteId)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['inbox-previews'] });
        })
        .catch((err) => {
          console.error('Erro ao marcar mensagens como lidas:', err);
        });
      // Diz à Meta que lemos (tiques azuis no cliente). Best-effort.
      if (ultimaClienteBspId) {
        void markWhatsAppRead(ultimaClienteBspId);
      }
    }
  }, [clienteId, mensagens, queryClient, ultimaClienteBspId]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 100;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    setIsAtBottom(atBottom);
    if (atBottom) setNewMessageCount(0);
  }, []);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      setNewMessageCount(0);
    }
  }, []);

  useEffect(() => {
    if (!mensagens) return;
    const currentCount = mensagens.length;
    const prevCount = prevMessageCountRef.current;

    if (currentCount > prevCount && prevCount > 0) {
      // New message(s) arrived. Snap to bottom only when the user was already
      // at the bottom — otherwise surface a "X new messages" pill.
      if (isAtBottomRef.current) {
        scrollToBottom();
      } else {
        setNewMessageCount((c) => c + (currentCount - prevCount));
      }
    } else if (prevCount === 0 && currentCount > 0) {
      // Initial load — anchor to bottom.
      scrollToBottom();
    }

    prevMessageCountRef.current = currentCount;
    // Intentionally NOT including isAtBottom — we read it via ref so the
    // effect doesn't re-run on every scroll event and fight the user.
  }, [mensagens, scrollToBottom]);

  const mensagensAgrupadas = useMemo(() => {
    if (!mensagens) return [];

    const grupos: { data: Date; mensagens: typeof mensagens }[] = [];
    let grupoAtual: typeof mensagens = [];
    let dataAtual: Date | null = null;

    mensagens.forEach((msg) => {
      const dataMensagem = new Date(msg.criado_em);

      if (!dataAtual || !isSameDay(dataAtual, dataMensagem)) {
        if (grupoAtual.length > 0 && dataAtual) {
          grupos.push({ data: dataAtual, mensagens: grupoAtual });
        }
        grupoAtual = [msg];
        dataAtual = dataMensagem;
      } else {
        grupoAtual.push(msg);
      }
    });

    if (grupoAtual.length > 0 && dataAtual) {
      grupos.push({ data: dataAtual, mensagens: grupoAtual });
    }

    return grupos;
  }, [mensagens]);

  if (!clienteId) {
    return (
      <div className="painel__convo-empty">
        <div className="painel__profile-contact-icn" style={{ width: 56, height: 56 }}>
          <MessageSquare size={24} />
        </div>
        <h3 className="text-base font-semibold mt-2">{t('panel.selectConversation')}</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          {t('panel.selectConversationDescription')}
        </p>
      </div>
    );
  }

  if (loadingCliente) {
    return (
      <>
        <div className="painel__convo-hdr">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div>
            <Skeleton className="h-4 w-32 mb-1" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <div className="painel__convo-body">
          <Skeleton className="h-14 w-48 rounded-2xl" />
          <Skeleton className="h-14 w-56 rounded-2xl ml-auto" />
          <Skeleton className="h-14 w-40 rounded-2xl" />
        </div>
      </>
    );
  }

  const nomeCliente = cliente?.nome || t('common.client');
  const nomeProprio = t('common.you');

  const inicial = nomeCliente.charAt(0).toUpperCase();
  const codigo = cliente?.codigo_cliente || cliente?.telefone?.replace(/@[cs]\.us$/, '');
  const abrirPerfil = () => navigate(`/cliente-potential/${clienteId}`);
  const canal = cliente ? canalDaConversa(cliente) : 'whatsapp';

  return (
    <>
      <header className="painel__convo-hdr">
        <button
          type="button"
          className="painel__convo-idbtn"
          onClick={abrirPerfil}
          aria-label={t('panel.viewProfile', 'Ver perfil')}
        >
          <span className="cli-itm__avt-img" style={{ width: 40, height: 40, fontSize: 14 }}>
            {cliente?.avatar_url ? <img src={cliente.avatar_url} alt="" /> : inicial}
          </span>
          <div>
            <div className="painel__convo-name">{cliente?.nome || t('common.client')}</div>
            <div className="painel__convo-meta">
              {codigo && <span className="mono">{codigo}</span>}
              {canal === 'app' && (
                <>
                  {codigo && <span> · </span>}
                  <span className="status-pill text-xs">App</span>
                </>
              )}
              {canal === 'whatsapp' && whatsAppContact && (
                <>
                  {codigo && <span> · </span>}
                  <span style={{ color: '#25D366', fontWeight: 600 }}>WhatsApp</span>
                </>
              )}
              <Janela24hBadge mensagens={mensagens} />
              {ehApp && otherTyping && (
                <span className="text-xs text-muted-foreground">{t('chat.typing')}</span>
              )}
            </div>
          </div>
        </button>
        <div className="painel__convo-hdr-actions">
          {!cliente?.responsavel_id ? (
            <button
              type="button"
              className="ow-btn ow-btn--primary ow-btn--sm"
              disabled={assumirAtendimento.isPending || !profile?.id}
              onClick={() => {
                if (!clienteId || !profile?.id) return;
                assumirAtendimento.mutate({
                  clienteId,
                  profileId: profile.id,
                  profileNome: profile.nome || t('common.role.atendente'),
                });
              }}
            >
              <UserPlus size={14} />
              {t('panel.takeOver')}
            </button>
          ) : (
            <>
              <span className="painel__convo-meta">
                {t('panel.responsible', { name: cliente.responsavel?.nome || t('common.unknown') })}
              </span>
              {clienteId && (
                <TransferirAtendimentoButton
                  clienteId={clienteId}
                  responsavelId={cliente.responsavel_id}
                  responsavelNome={cliente.responsavel?.nome}
                />
              )}
            </>
          )}
        </div>
      </header>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="painel__convo-body"
        style={{ position: 'relative' }}
      >
        {loadingMensagens ? (
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                className="h-14 w-48 rounded-2xl"
                style={{ alignSelf: i % 2 === 0 ? 'flex-end' : 'flex-start' }}
              />
            ))}
          </div>
        ) : mensagens?.length === 0 ? (
          <div className="painel__convo-empty">
            <MessageSquare size={32} className="text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">{t('panel.noMessagesYet')}</p>
            <p className="text-xs text-muted-foreground">{t('panel.sendFirstMessage')}</p>
          </div>
        ) : (
          mensagensAgrupadas.map((grupo, idx) => (
            <div key={idx} className="flex flex-col">
              <div className="painel__convo-day">
                {format(grupo.data, "d 'de' MMMM", { locale: ptBR })}
              </div>
              {grupo.mensagens.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  conteudo={msg.conteudo}
                  remetente={msg.remetente}
                  nomeRemetente={msg.nome_remetente || undefined}
                  avatarUrl={cliente?.avatar_url ?? null}
                  dataHora={msg.criado_em}
                  isOwn={msg.remetente === 'atendente'}
                  canal={msg.canal as 'interno' | 'whatsapp' | 'app'}
                  statusEntrega={
                    msg.status_entrega as 'sent' | 'delivered' | 'read' | 'failed' | null
                  }
                  mensagemId={msg.id}
                  tipoMedia={msg.tipo_media as 'image' | 'audio' | 'video' | 'document' | null}
                  mediaStatus={msg.media_status as 'pending' | 'ready' | 'failed' | null}
                  mediaStoragePath={msg.media_storage_path}
                  mediaMimeType={msg.media_mime_type}
                  mediaFilename={msg.media_filename}
                  bspMessageId={msg.bsp_message_id}
                  lidaConsumer={msg.lida_consumer ?? false}
                  quoted={resolverCitacao(mensagens ?? [], msg.reply_to_bsp_id ?? null, nomeCliente, nomeProprio)}
                  onReply={() => setRespostaA(msg)}
                  reacao={msg.reacao}
                  onReact={(emoji) =>
                    reagir.mutate({
                      clienteId: clienteId!,
                      mensagemId: msg.id,
                      bspMessageId: msg.bsp_message_id!,
                      emoji: toggleReaction(msg.reacao, emoji) ?? '',
                    })
                  }
                />
              ))}
            </div>
          ))
        )}

        {newMessageCount > 0 && !isAtBottom && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-foreground text-background px-3 py-1.5 rounded-full shadow-pill text-xs font-semibold"
          >
            <ChevronDown size={12} />
            {newMessageCount} {newMessageCount === 1 ? 'nova mensagem' : 'novas mensagens'}
          </button>
        )}
      </div>

      <ChatInput
        clienteId={clienteId}
        canal={canal}
        responsavelId={cliente?.responsavel_id ?? null}
        ultimaClienteBspId={ultimaClienteBspId}
        respostaCitacao={respostaA ? previewDeMensagem(respostaA, nomeCliente, nomeProprio) : null}
        respostaBspId={respostaA?.bsp_message_id ?? null}
        onCancelarResposta={() => setRespostaA(null)}
        onTyping={ehApp ? notifyTyping : undefined}
      />
    </>
  );
}
